import { HttpClient } from '@angular/common/http'
import { Inject, Injectable, inject } from '@angular/core'

import { Observable } from 'rxjs'

import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'

import { AiAgent, AiDocumentContentType, AiDocumentStatus } from '../../../common/ai.dictionary'

export type ContextDocumentContent = {
  name: string
  description: string
  type: AiDocumentContentType
  value: string
  status: AiDocumentStatus
  images: any[]
  document_desc: string
  document_type: string
  summary: string
  content: ContextDocumentContent[]
}

export type ContextDocumentResponse = [
  {
    id: string
    resource_id?: string
    name: string
    file_name: string
    created_at: string
    updated_at: string
    status: AiDocumentStatus
    file_properties: {
      file_type: string
      object_name?: string
    }
    document_type: string
    document_desc: string
    db_collection: string
    details: {
      summary: string
      content: ContextDocumentContent[]
      images: []
    }
  },
]

export type DocumentUploadResponse = {
  files_status: {
    unique_id: string
    fileName: string
    response: {
      summary: string
      document_desc: string
      document_type: string
      images: any[]
      name: string
      content: ContextDocumentContent[]
    }
    status: AiDocumentStatus
  }[]
}

export type ExtractFromDocumentPayload = {
  unique_id: string
  object_name: string
  filename: string
  content_type: string
  application_id: string
  application_version_id: string
  workspace_id?: string
  resource_id: string
  wait_for_summary: boolean
  session_id?: string
  new_model: boolean
}

@Injectable({
  providedIn: 'root',
})
export class AiDocumentsApi extends ApiHttpService {
  #urlParametersStoreService = inject(UrlParametersStoreService)
  #diPlatformV1 = `di-platform/api/v1/di-platform`

  // eslint-disable-next-line @angular-eslint/prefer-inject -- super() needs positional args; can't use inject() here
  constructor(@Inject('BASE_URL') baseUrl: string, httpClient: HttpClient) {
    super(baseUrl, httpClient)
  }

  getDocuments(agent: AiAgent): Observable<ContextDocumentResponse> {
    return this.post(`${this.#diPlatformV1}/documents/byAgent`, {
      agent_name: agent,
      application_id: this.#urlParametersStoreService.urlParameters.appId,
    })
  }

  getDocumentsByAppId(appId: string): Observable<ContextDocumentResponse> {
    return this.get(`${this.#diPlatformV1}/documents/${appId}`)
  }

  extractFromDocument(payload: ExtractFromDocumentPayload): Observable<DocumentUploadResponse> {
    return this.post(`${this.#diPlatformV1}/files/extract`, payload)
  }

  extractFromDocumentSSE(payload: ExtractFromDocumentPayload): Observable<string> {
    return this.stream(`${this.#diPlatformV1}/files/extract/sse`, payload)
  }

  extractFromUpload(payload: FormData): Observable<DocumentUploadResponse> {
    return this.post(`${this.#diPlatformV1}/files/upload`, payload)
  }

  deleteDocumentById(documentId: string): Observable<void> {
    return this.delete(`${this.#diPlatformV1}/documents/${documentId}`)
  }
}
